<template>
    <div class="lg:grid lg:grid-cols-7 lg:grid-rows-1 lg:gap-x-8 lg:gap-y-10 xl:gap-x-16">
        <!-- Product image -->
        <div class="lg:col-span-4 lg:row-end-1 text-center">
            <div class="relative group w-full max-w-2xl mx-auto">
                <Labels :product="product"/>
                <div class="aspect-h-1 aspect-w-1 rounded-lg bg-white overflow-hidden -z-10">
                    <img :src="images[selectedImage]" :alt="product.title" :class="imageClasses" class="object-contain"/>
                </div>
            </div>
            <div v-if="images.length > 1" class="flex justify-center gap-2 mt-3">
                <button
                    v-for="(img, index) in images"
                    :key="index"
                    @click="selectedImage = index"
                    class="rounded-md overflow-hidden border-2 transition"
                    :class="selectedImage === index ? 'border-indigo-500' : 'border-transparent hover:border-gray-300'"
                >
                    <img :src="imageSize(img, 124)" :alt="`${product.title} image ${index + 1}`" class="w-16 h-16 object-cover"/>
                </button>
            </div>
        </div>
        <!-- Product details -->
        <div class="lg:col-span-3 lg:row-span-2 lg:row-end-2">
            <div class="mt-4">
                <Link v-if="product.details.manufacturer" class="text-lg font-bold tracking-tight text-gray-400"
                      :href="product.details.manufacturer.url">
                    {{ product.details.manufacturer.name }}
                </Link>
                <h1 class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{{ product.title }}</h1>
            </div>
            <template v-if="product.price > 1">
                <p v-if="product.inStock" class="text-3xl text-gray-900 leading-tight">
                    <PriceWithUnits :product="product"/>
                </p>
                <div class="flex flex-col max-w-sm mt-2 space-y-1">
                    <CartButton v-if="product.inStock" :productId="product.productId"
                                class="w-full" text-class="text-lg"/>
                    <SpecialButtons :product="product" class="w-full"/>
                </div>
            </template>
            <div class="my-6 flex items-center">
                <StockDetail :product="product"/>
            </div>
            <dl class="grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-8 sm:grid-cols-2">
                <div class="sm:col-span-2 border-t border-gray-200 pt-6 mt-6">
                    <dd class="mt-1 text-gray-900 prose prose-sm" ref="description" v-html="product.description"/>
                </div>
                <div class="sm:col-span-2" v-if="product.details.fabricLine?.description">
                    <dd class="mt-1 text-sm text-gray-900">{{ product.title }} is part of
                        {{ product.details.fabricLine.name }}, {{ lowercaseFirst(product.details.fabricLine.description) }}
                    </dd>
                </div>
                <div class="sm:col-span-2 border-t border-gray-200 pt-2">
                    <h2 class="sm:hidden group relative flex w-full items-center justify-between py-3 text-left font-medium text-gray-900 cursor-pointer"
                        @click="showDetails = !showDetails">
                            <span
                                :class="[showDetails ? 'text-indigo-600' : 'text-gray-900']">Details</span>
                        <span class="ml-6 flex items-center">
                              <PhPlus v-if="!showDetails"
                                      class="block h-6 w-6 text-gray-400 group-hover:text-gray-500"
                                      aria-hidden="true"/>
                              <PhMinus v-else
                                       class="block h-6 w-6 text-indigo-400 group-hover:text-indigo-500"
                                       aria-hidden="true"/>
                        </span>
                    </h2>
                </div>
                <template v-for="(details, key) in product.details">
                    <div :class="showDetails ? '' : 'hidden'"
                         class="sm:col-span-1 sm:block"
                         v-if="details && (!(details instanceof Array) || details.length > 0)">
                        <dt class="text-sm font-medium text-gray-500">{{ humanizeString(key) }}</dt>
                        <dd class="mt-1 text-sm text-gray-900">
                            <template v-if="details instanceof Array" v-for="(item,index) in details">
                                <Link :href="item.url">{{ item.name }}</Link>
                                <span v-if="index !== details.length - 1">, </span>
                            </template>
                            <Link v-else-if="details.url && details.name" :href="details.url">
                                {{ details.name }}
                            </Link>
                            <template v-else>{{ details }}</template>
                        </dd>
                    </div>
                </template>
                <template v-if="admin">
                    <div class="sm:col-span-2 border-t border-gray-200 pt-4 mt-4">
                        <h3 class="text-sm font-semibold text-indigo-600">Admin</h3>
                    </div>
                    <div class="sm:col-span-1 sm:block">
                        <dt class="text-sm font-medium text-gray-500">Image</dt>
                        <dd class="mt-1 text-sm text-gray-900 flex items-center gap-2">
                            <input type="text" readonly :value="product.imageSrc" class="flex-1 min-w-0 text-sm bg-gray-50 border border-gray-200 rounded px-2 py-1"/>
                            <button @click="copyImageUrl" class="flex-shrink-0 p-1 rounded hover:bg-gray-100" title="Copy URL">
                                <PhCheck v-if="imageCopied" class="h-4 w-4 text-green-600"/>
                                <PhCopy v-else class="h-4 w-4 text-gray-500 hover:text-gray-700"/>
                            </button>
                        </dd>
                    </div>
                    <div class="sm:col-span-1 sm:block">
                        <dt class="text-sm font-medium text-gray-500">Popularity</dt>
                        <dd class="mt-1 text-sm text-gray-900 flex">
                            {{ product.popularity }}
                        </dd>
                    </div>
                </template>
                <hr class="-mt-2 sm:hidden"/>
            </dl>
        </div>
    </div>
</template>
<script setup>
import {computed, onMounted, ref} from 'vue';
import {Link, usePage} from '@inertiajs/vue3';
import PriceWithUnits from '@/Components/PriceWithUnits.vue';
import CartButton from '@/Components/CartButton.vue';
import Labels from '@/Components/Labels.vue';
import humanizeString from 'humanize-string';
import StockDetail from '@/Components/StockDetail.vue';
import SpecialButtons from '@/Components/SpecialButtons.vue';
import {PhPlus, PhMinus, PhCopy, PhCheck} from "@phosphor-icons/vue";
import {imageSize} from "@/mixins";

const admin = computed(() => usePage().props.auth.admin);

const props = defineProps({
    product: Object,
});

const images = computed(() => props.product.images?.length ? props.product.images : [props.product.imageSrc]);
const selectedImage = ref(0);

const showDetails = ref(false);
const imageCopied = ref(false);

const description = ref(null);

const copyImageUrl = async () => {
    await navigator.clipboard.writeText(props.product.imageSrc);
    imageCopied.value = true;
    setTimeout(() => imageCopied.value = false, 2000);
};

const imageClasses = computed(() => props.product.cropImage ? 'group-hover:scale-150 transition transition-duration-300' : '');

const lowercaseFirst = (str) => str.charAt(0).toLowerCase() + str.slice(1);

onMounted(() => {
    // Find the <a> element with class 'button' inside the parent element
    const buttonElement = description.value?.querySelector('a.button');

    // Add a class to the found <a> element
    if (buttonElement) {
        buttonElement.classList.add(...'no-underline inline-flex items-center justify-center text-center px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition ease-in-out duration-150 py-2 font-semibold text-white bg-gray-800 border border-transparent hover:bg-gray-700 focus:bg-gray-700 active:bg-gray-900'.split(' '));
    }
});
</script>
